// IntelligenceLogin.js — Content Intelligence Specialist login.
//
// POSTs /api/staff/login → stores JWT (tribal_staff_token) + staff
// profile (tribal_staff_user) → redirects to /intelligence.
//
// Anti-enumeration alignment: we surface ONLY err.response?.data?.error
// (the backend returns 'Invalid credentials' uniformly for wrong email,
// wrong password, deactivated staff). Do NOT add any client-side
// distinguishing message — that would defeat the timing/response
// uniformity the backend is enforcing.
//
// Distinct token keys (tribal_staff_token / tribal_staff_user) prevent
// collision with creator auth: if both a creator and a specialist sign
// in on the same browser, neither clobbers the other's session, and
// the creator-side PrivateRoute won't accept a staff token by accident.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = 'https://tribaltech-backend.onrender.com';

export default function IntelligenceLogin() {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/api/staff/login`,
        { email, password });
      localStorage.setItem('tribal_staff_token', data.token);
      localStorage.setItem('tribal_staff_user',  JSON.stringify(data.staff));
      window.location.href = '/intelligence';
    } catch (err) {
      // ONLY the backend's response message — never add a distinguishing
      // client-side string. Backend returns 'Invalid credentials' uniformly
      // for missing email / wrong email / wrong password / deactivated
      // staff. Anti-enumeration.
      setError(err.response?.data?.error || 'Something went wrong');
      setLoading(false);
    }
  }

  return (
    <div style={page}>
      <div style={card}>
        <div style={eyebrow}>Creator Protect · Content Intelligence</div>
        <h1 style={{ color: '#fff', fontSize: 24, margin: '6px 0 6px' }}>
          Sign in to your specialist workspace
        </h1>
        <div style={{ color: '#888', fontSize: 13, marginBottom: 22 }}>
          Operator-provisioned accounts only. Contact an admin to be added.
        </div>

        <form onSubmit={handleSubmit}>
          <label style={label}>Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com"
            disabled={loading}
            style={input}
          />

          <label style={label}>Password</label>
          <input
            type="password"
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••"
            disabled={loading}
            style={input}
          />

          {error && (
            <div style={errorBox}>
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} style={button}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div style={{ marginTop: 28, fontSize: 12, color: '#666', textAlign: 'center' }}>
          Wrong place?{' '}
          <Link to="/login" style={{ color: '#a78bfa' }}>Creator login →</Link>
        </div>
      </div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────
const page = {
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #0f0f1e 0%, #0a0a0f 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 20px',
};

const card = {
  background: '#111',
  borderRadius: 16,
  padding: 32,
  maxWidth: 440,
  width: '100%',
  border: '1px solid #222',
};

const eyebrow = {
  fontSize: 11,
  color: '#888',
  letterSpacing: 2,
  textTransform: 'uppercase',
  marginBottom: 6,
};

const label = {
  display: 'block',
  fontSize: 12,
  color: '#94a3b8',
  marginBottom: 6,
  marginTop: 12,
};

const input = {
  width: '100%',
  padding: 12,
  borderRadius: 8,
  background: '#0a0a0a',
  border: '1px solid #222',
  color: '#fff',
  fontSize: 14,
  boxSizing: 'border-box',
  marginBottom: 4,
};

const button = {
  width: '100%',
  padding: 14,
  marginTop: 18,
  borderRadius: 8,
  border: 'none',
  background: '#7c3aed',
  color: '#fff',
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
};

const errorBox = {
  marginTop: 14,
  padding: '10px 14px',
  background: 'rgba(248,113,113,0.08)',
  border: '1px solid rgba(248,113,113,0.3)',
  borderRadius: 8,
  color: '#f87171',
  fontSize: 13,
};
